/* =============================================================
   Anonymous-mode storage for the Psych Safety Assessment.

   When a user is signed in we read and write via the queries.ts
   hooks (Azure Functions /api). When they're anonymous we read
   and write via the helpers below — sessionStorage, so a guest's
   answers last only until the tab closes (only members save).
   Sign-in happens in the same tab, so the answers survive it.

   On sign-in we migrate any local draft / result into the DB,
   then clear the local copies.
   ============================================================ */

import {
  allStageAverages,
  overallAverage,
  overallZoneCounts,
  type Responses,
} from './psychSafety';
import { apiFetch } from './api';

const KEY_DRAFT  = 'ps:draft:v2';
const KEY_RESULT = 'ps:result:v2';

export interface LocalDraft {
  responses: Responses;
  stage_idx: number;
  updated_at: string;
}

export interface LocalResult {
  responses: Responses;
  completed_at: string;
}

function safeGet<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private-mode Safari etc — silently ignore */
  }
}

function safeRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------
// Drafts
// ---------------------------------------------------------------
export function getLocalDraft(): LocalDraft | null {
  return safeGet<LocalDraft>(KEY_DRAFT);
}

export function setLocalDraft(d: { responses: Responses; stage_idx: number }): void {
  const payload: LocalDraft = {
    responses: d.responses,
    stage_idx: d.stage_idx,
    updated_at: new Date().toISOString(),
  };
  safeSet(KEY_DRAFT, payload);
}

export function clearLocalDraft(): void {
  safeRemove(KEY_DRAFT);
}

// ---------------------------------------------------------------
// Results
// ---------------------------------------------------------------
export function getLocalResult(): LocalResult | null {
  return safeGet<LocalResult>(KEY_RESULT);
}

export function setLocalResult(r: Responses): void {
  safeSet(KEY_RESULT, {
    responses: r,
    completed_at: new Date().toISOString(),
  } satisfies LocalResult);
  clearLocalDraft();
}

export function clearLocalResult(): void {
  safeRemove(KEY_RESULT);
}

// ---------------------------------------------------------------
// Sign-in migration — promote local state into the DB.
// ---------------------------------------------------------------

/**
 * If the user just signed in and has a local result or draft, save
 * them to the server under their new account, then clear local
 * copies. Idempotent: safe to call any time after sign-in.
 */
export async function migrateLocalToServer(_userId: string): Promise<{
  result_migrated: boolean;
  draft_migrated: boolean;
}> {
  let result_migrated = false;
  let draft_migrated = false;

  const localResult = getLocalResult();
  if (localResult && Object.keys(localResult.responses).length > 0) {
    const overall = overallAverage(localResult.responses);
    const stage_scores = allStageAverages(localResult.responses);
    const zc = overallZoneCounts(localResult.responses);
    try {
      await apiFetch('/api/assessments', {
        method: 'POST',
        body: {
          responses: localResult.responses,
          overall_score: Number(overall.toFixed(2)),
          stage_scores,
          zone_counts: {
            red: zc.red,
            neutral: zc.neutral,
            blue: zc.blue,
            total: zc.total,
          },
        },
      });
      result_migrated = true;
      clearLocalResult();
    } catch (e) {
      console.warn('[psych] result migrate failed:', e);
    }
  } else {
    const localDraft = getLocalDraft();
    if (localDraft && Object.keys(localDraft.responses).length > 0) {
      try {
        await apiFetch('/api/assessments/draft', {
          method: 'PUT',
          body: {
            responses: localDraft.responses,
            stage_idx: localDraft.stage_idx,
          },
        });
        draft_migrated = true;
        clearLocalDraft();
      } catch (e) {
        console.warn('[psych] draft migrate failed:', e);
      }
    }
  }

  return { result_migrated, draft_migrated };
}
